import * as React from "react";
import { toast } from "sonner";

import {
  SESSION_EXPIRED_EVENT,
  buildSignInRedirectPath,
  type SessionExpiredEventDetail,
  type SessionExpiredReason,
} from "@/lib/sessionExpired";

type SessionExpiredContextValue = {
  open: boolean;
  reason: SessionExpiredReason;
  showSessionExpired: (reason?: SessionExpiredReason) => void;
  signInAgain: () => void;
};

const SessionExpiredContext = React.createContext<SessionExpiredContextValue | null>(
  null,
);

export function SessionExpiredProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState<SessionExpiredReason>("expired");

  const showSessionExpired = React.useCallback((nextReason: SessionExpiredReason = "expired") => {
    toast.dismiss();
    setReason(nextReason);
    setOpen(true);
  }, []);

  const signInAgain = React.useCallback(() => {
    toast.dismiss();
    setOpen(false);
    window.location.assign(buildSignInRedirectPath());
  }, []);

  React.useEffect(() => {
    const onExpired = (event: Event) => {
      const detail = (event as CustomEvent<SessionExpiredEventDetail>).detail;
      toast.dismiss();
      setReason(detail?.reason ?? "expired");
      setOpen(true);
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  const value = React.useMemo(
    () => ({ open, reason, showSessionExpired, signInAgain }),
    [open, reason, showSessionExpired, signInAgain],
  );

  return (
    <SessionExpiredContext.Provider value={value}>
      {children}
    </SessionExpiredContext.Provider>
  );
}

export function useSessionExpired(): SessionExpiredContextValue {
  const ctx = React.useContext(SessionExpiredContext);
  if (!ctx) {
    throw new Error("useSessionExpired must be used within SessionExpiredProvider");
  }
  return ctx;
}
